import React from 'react';
import styled from 'styled-components';
import { useSubmissionsStats } from '../state/hooks';
import { useClassInsights } from './ClassInsightsContext';
import { catalogClasses } from './catalogSelectors';
import { emptyStateCopy } from './emptyStateCopy';
import { statsBody } from './useFilters';

// `compact` fits a KPI card; the full size keeps the ci-empty-state class, which the
// panel layouts and the PDF screenshot export look for.
export function EmptyState({ message, detail, compact = false }) {
  return (
    <EmptyStateWrapper
      className={compact ? 'ci-kpi-empty' : 'ci-empty-state'}
      $compact={compact}
    >
      <EmptyStateMessage>{message}</EmptyStateMessage>
      {detail && <EmptyStateDetail>{detail}</EmptyStateDetail>}
    </EmptyStateWrapper>
  );
}

// The submissions count shares the KPI cards' query (same key), so this adds no request.
// While that query refreshes it still holds the previous filters' count, so it is ignored.
export function ToolEmptyState({ tool }) {
  const { debouncedFilters, catalog } = useClassInsights();
  const hasClasses = catalogClasses(catalog).length > 0;
  const { data, isFetching, isPreviousData } = useSubmissionsStats(
    statsBody(debouncedFilters),
    hasClasses
  );
  const settled = Boolean(data) && !isFetching && !isPreviousData;
  const copy = emptyStateCopy({
    tool,
    hasClasses,
    classwork: settled ? data.classwork : undefined,
    unavailable: Boolean(catalog?.unavailable || (settled && data.unavailable)),
  });
  return <EmptyState {...copy} />;
}

const EmptyStateWrapper = styled.div`
  flex-direction: column;
  gap: 6px;
  text-align: center;
  ${({ $compact }) =>
    $compact &&
    `
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 80px;
  `}
`;

const EmptyStateMessage = styled.div`
  color: var(--river-bed);
  font-size: 14px;
  font-weight: 600;
`;

const EmptyStateDetail = styled.div`
  color: var(--chicago);
  font-size: 13px;
  max-width: 320px;
`;
